/**
 * Stock Adjustment Form Component
 * Modal form for creating/editing stock adjustments with scrollable content
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import adminApi from '../../../services/adminApi';
import inventoryApi from '../../../services/inventoryApi';
import SearchableSelect from '../../Common/SearchableSelect';
import SearchablePagedDropdown from '../../Common/SearchablePagedDropdown';
import PermissionGuard from '../../Common/PermissionGuard';
import useClosedPeriod from '../../../hooks/useClosedPeriod';

const StockAdjustmentForm = ({ stockAdjustment, viewMode = false, onSuccess, onCancel, onApprove, onReject, onEdit }) => {
  // Label of the selected item (the item list is loaded page by page as the user searches / scrolls)
  const [itemLabel, setItemLabel] = useState('');

  const [formData, setFormData] = useState({
    item: '',
    warehouse: '',
    adjustmentType: 'increase', // 'increase', 'decrease', 'set'
    quantity: '',
    newQuantity: '', // For 'set' type
    reason: '',
    reasonCode: 'manual_adjustment',
    notes: '',
    reference: '',
    unitCost: '',
    adjustmentDate: new Date().toISOString().slice(0, 10)
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentStock, setCurrentStock] = useState(0);
  // Cost the adjustment is valued at: average cost of the stock in the warehouse, else the item cost price
  const [averageCost, setAverageCost] = useState(0);
  const [costSource, setCostSource] = useState('');
  // true once the user typed a cost (or a saved adjustment brought one) — then it is not overwritten
  const costTouched = React.useRef(false);
  const [warehouses, setWarehouses] = useState([]);

  const loadWarehouses = async () => {
    try {
      // Get all locations and filter for warehouse type
      const response = await adminApi.getLocations({ type: 'warehouse', limit: 1000 });
      // Handle different response structures
      const warehousesData = response.data?.data || response.data || [];
      setWarehouses(warehousesData);
    } catch (error) {
      console.error('Error loading warehouses:', error);
      // Fallback: get all locations without type filter
      try {
        const fallbackResponse = await adminApi.getLocations({ limit: 1000 });
        const allLocations = fallbackResponse.data?.data || fallbackResponse.data || [];
        const warehouses = allLocations.filter(location => location.type === 'warehouse');
        setWarehouses(warehouses);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        setWarehouses([]);
      }
    }
  };

  const { minDate, dateError } = useClosedPeriod();

  const closedError = dateError(formData.adjustmentDate);


  useEffect(() => {
    loadWarehouses();
  }, []);

  // Editing / viewing: the list only has a summary — load the whole adjustment
  const [fullAdjustment, setFullAdjustment] = useState(null);
  useEffect(() => {
    if (!stockAdjustment?._id) { setFullAdjustment(null); return; }
    inventoryApi.getStockAdjustmentById(stockAdjustment._id)
      .then(res => setFullAdjustment(res?.data || stockAdjustment))
      .catch(() => setFullAdjustment(stockAdjustment));
  }, [stockAdjustment?._id]);

  useEffect(() => {
    const stockAdjustment = fullAdjustment;
    if (stockAdjustment) {
      // label of the saved item for the paged dropdown
      const savedItem = stockAdjustment.item;
      setItemLabel(savedItem?.name ? `${savedItem.name}${savedItem.sku ? ` (${savedItem.sku})` : ''}` : '');
      // a saved adjustment keeps its own cost (the lookup does not overwrite it)
      costTouched.current = stockAdjustment.unitCost != null;
      setFormData({
        item: stockAdjustment.item?._id || stockAdjustment.item || '',
        warehouse: stockAdjustment.warehouse?._id || stockAdjustment.warehouse || '',
        adjustmentType: stockAdjustment.adjustmentType || 'increase',
        quantity: stockAdjustment.quantity?.toString() || '',
        newQuantity: stockAdjustment.newQuantity?.toString() || '',
        reason: stockAdjustment.reason || '',
        reasonCode: stockAdjustment.reasonCode || 'manual_adjustment',
        notes: stockAdjustment.notes || '',
        reference: stockAdjustment.reference || '',
        unitCost: stockAdjustment.unitCost?.toString() || '',
        adjustmentDate: (stockAdjustment.adjustmentDate || stockAdjustment.createdAt)
          ? new Date(stockAdjustment.adjustmentDate || stockAdjustment.createdAt).toISOString().slice(0, 10)
          : new Date().toISOString().slice(0, 10)
      });
    }
  }, [fullAdjustment]);

  // Fetch current stock when item/warehouse changes
  useEffect(() => {
    const fetchCurrentStock = async () => {
      if (formData.item && formData.warehouse) {
        try {
          const stockParams = {
            item: formData.item,
            warehouse: formData.warehouse,
            limit: 1000
          };

          const stockResponse = await inventoryApi.getStockLevels(stockParams);

          // Stock for an item/warehouse can be split across multiple StockLevel
          // documents (one per location, e.g. from GRN receipts) — sum them all
          // rather than reading a single arbitrary record.
          const records = stockResponse.data || [];
          const totalQuantity = records.reduce((sum, s) => sum + (s.quantity || 0), 0);
          setCurrentStock(totalQuantity);

          // Average cost of the stock on hand (value-weighted across location rows);
          // no stock in this warehouse yet → the item's cost price
          const totalValue = records.reduce((sum, s) => sum + (s.quantity || 0) * (s.cost?.unitCost || 0), 0);
          let cost = totalQuantity > 0 ? totalValue / totalQuantity : 0;
          let source = cost > 0 ? 'Average cost of the stock in this warehouse' : '';
          if (!(cost > 0)) {
            const withCost = records.find(s => (s.cost?.unitCost || 0) > 0);
            if (withCost) { cost = withCost.cost.unitCost; source = 'Last stock cost in this warehouse'; }
          }
          if (!(cost > 0)) {
            const itemRes = await inventoryApi.getItemById(formData.item).catch(() => null);
            const price = Number(itemRes?.data?.pricing?.unitCost) || 0;
            if (price > 0) { cost = price; source = 'Item cost price (no stock in this warehouse yet)'; }
          }
          cost = Math.round((cost || 0) * 100) / 100;
          setAverageCost(cost);
          setCostSource(cost > 0 ? source : 'No cost found — enter the unit cost');
          if (!costTouched.current) {
            setFormData(prev => ({ ...prev, unitCost: cost > 0 ? String(cost) : '' }));
          }
        } catch (error) {
          console.error('Error fetching current stock:', error);
          setCurrentStock(0);
          setAverageCost(0);
          setCostSource('');
        }
      } else {
        setCurrentStock(0);
        setAverageCost(0);
        setCostSource('');
      }
    };

    fetchCurrentStock();
  }, [formData.item, formData.warehouse]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.item) {
      newErrors.item = 'Item is required';
    }

    if (!formData.warehouse) {
      newErrors.warehouse = 'Warehouse is required';
    }

    if (formData.adjustmentType === 'set') {
      if (!formData.newQuantity || formData.newQuantity < 0) {
        newErrors.newQuantity = 'New quantity must be 0 or greater';
      }
    } else {
      if (!formData.quantity || formData.quantity <= 0) {
        newErrors.quantity = 'Adjustment quantity must be greater than 0';
      }
    }

    if (!formData.reason.trim()) {
      newErrors.reason = 'Reason is required';
    }

    if (formData.adjustmentDate && formData.adjustmentDate > new Date().toISOString().slice(0, 10)) {
      newErrors.adjustmentDate = 'Adjustment date cannot be in the future';
    }

    if (formData.unitCost && formData.unitCost < 0) {
      newErrors.unitCost = 'Unit cost cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'unitCost') costTouched.current = true;
    if (name === 'item' || name === 'warehouse') costTouched.current = false;
    // Trim string values for select fields to prevent whitespace issues
    const cleanValue = (name === 'adjustmentType' || name === 'reasonCode') && typeof value === 'string'
      ? value.trim()
      : value;

    setFormData(prev => ({
      ...prev,
      [name]: cleanValue
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Prepare submission data for StockAdjustment API
      const submitData = {
        item: formData.item,
        warehouse: formData.warehouse,
        location: formData.location || '',
        adjustmentType: formData.adjustmentType.trim(),
        reason: formData.reason.trim(),
        reasonCode: formData.reasonCode.trim(),
        notes: formData.notes.trim(),
        reference: formData.reference.trim(),
        adjustmentDate: formData.adjustmentDate || undefined
      };

      // Add quantity based on adjustment type
      if (formData.adjustmentType === 'set') {
        submitData.newQuantity = parseFloat(formData.newQuantity);
      } else {
        submitData.quantity = parseFloat(formData.quantity);
      }

      // Unit cost only for an increase (a decrease is valued at the average stock cost)
      if (formData.adjustmentType === 'increase' && formData.unitCost && !isNaN(parseFloat(formData.unitCost)) && parseFloat(formData.unitCost) >= 0) {
        submitData.unitCost = parseFloat(formData.unitCost);
      }

      // Edit updates the pending adjustment; otherwise a new one (pending approval)
      if (stockAdjustment?._id) {
        await inventoryApi.updateStockAdjustment(stockAdjustment._id, submitData);
      } else {
        await inventoryApi.createStockAdjustment(submitData);
      }
      onSuccess();
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to adjust stock');
    } finally {
      setLoading(false);
    }
  };

  // Items are searched and loaded page by page from the server (active stock items only)
  const fetchItemPage = async (search, page, limit) => {
    const res = await inventoryApi.getItems({ search: search || undefined, page, limit, isActive: 'true', sortBy: 'name', sortOrder: 'asc' });
    const list = (Array.isArray(res?.data) ? res.data : res?.data?.items || [])
      .filter(item => item.type !== 'asset' && item.inventory?.trackInventory !== false);
    const pag = res?.pagination || {};
    return { items: list, page: pag.page || page, pages: pag.pages || pag.totalPages || 1, total: pag.total || pag.totalItems || list.length };
  };

  const warehouseOptions = ((warehouses && Array.isArray(warehouses)) ? warehouses : []).map(warehouse => ({
    value: warehouse._id,
    label: `${warehouse.name} (${warehouse.code})`
  }));

  const reasonCodeOptions = [
    { value: 'manual_adjustment', label: 'Manual Adjustment' },
    { value: 'damaged_goods', label: 'Damaged Goods' },
    { value: 'lost_items', label: 'Lost Items' },
    { value: 'found_items', label: 'Found Items' },
    { value: 'theft', label: 'Theft' },
    { value: 'expiry', label: 'Expired Items' },
    { value: 'quality_control', label: 'Quality Control' },
    { value: 'inventory_count', label: 'Inventory Count Correction' },
    { value: 'production_use', label: 'Production Use' },
    { value: 'other', label: 'Other' }
  ];

  const adjustmentTypeOptions = [
    { value: 'increase', label: 'Increase Stock' },
    { value: 'decrease', label: 'Decrease Stock' },
    { value: 'set', label: 'Set Exact Amount' }
  ];

  // Cost used: an increase uses the cost in the field; a decrease / set lower is valued at the average
  const costUsed = () => {
    const change = calculateNewStock() - currentStock;
    if (change > 0) return parseFloat(formData.unitCost) || averageCost || 0;
    return averageCost || 0;
  };
  const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const calculateNewStock = () => {
    if (formData.adjustmentType === 'set') {
      return parseFloat(formData.newQuantity) || 0;
    } else if (formData.adjustmentType === 'increase') {
      return currentStock + (parseFloat(formData.quantity) || 0);
    } else if (formData.adjustmentType === 'decrease') {
      return Math.max(0, currentStock - (parseFloat(formData.quantity) || 0));
    }
    return currentStock;
  };

  const field = (label, name, type = 'text', opts = {}) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}{opts.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={formData[name]}
        onChange={handleChange}
        placeholder={opts.placeholder || ''}
        disabled={viewMode || opts.disabled}
        step={opts.step}
        min={opts.min}
        className={`w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
          errors[name] ? 'border-red-300 bg-red-50' : 'border-gray-300'
        } ${(viewMode || opts.disabled) ? 'bg-gray-100' : ''} ${opts.right ? 'text-right' : ''}`}
      />
      {errors[name] && <p className="text-[10px] text-red-500 mt-0.5">{errors[name]}</p>}
      {opts.help && <p className="text-[10px] text-gray-400 mt-0.5">{opts.help}</p>}
    </div>
  );

  const select = (label, name, options, opts = {}) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}{opts.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <SearchableSelect
        name={name}
        value={formData[name]}
        onChange={handleChange}
        options={options}
        placeholder={opts.placeholder || `Select ${label.toLowerCase()}…`}
        disabled={viewMode || opts.disabled}
        error={errors[name] || ''}
        size="sm"
      />
      {opts.help && <p className="text-[10px] text-gray-400 mt-0.5">{opts.help}</p>}
    </div>
  );

  const textarea = (label, name, opts = {}) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}{opts.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <textarea
        name={name}
        rows={opts.rows || 3}
        value={formData[name]}
        onChange={handleChange}
        placeholder={opts.placeholder || ''}
        disabled={viewMode}
        className={`w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none ${
          errors[name] ? 'border-red-300 bg-red-50' : 'border-gray-300'
        } ${viewMode ? 'bg-gray-100' : ''}`}
      />
      {errors[name] && <p className="text-[10px] text-red-500 mt-0.5">{errors[name]}</p>}
      {opts.help && <p className="text-[10px] text-gray-400 mt-0.5">{opts.help}</p>}
    </div>
  );

  const sectionHeader = (title) => (
    <div className="flex items-center gap-2 mb-2">
      <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">{title}</span>
      <div className="flex-1 h-px bg-indigo-100" />
    </div>
  );

  const formIcon = (
    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/40 overflow-y-auto z-50 flex items-center justify-center p-4" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-white shadow-2xl border-0 flex flex-col rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                {formIcon}
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white" id="modal-title">
                  {viewMode ? 'View Stock Adjustment' : (stockAdjustment ? 'Edit Stock Adjustment' : 'Create Stock Adjustment')}
                </h3>
                <p className="text-indigo-100 text-sm mt-1">
                  {viewMode ? 'Review stock adjustment details' : (stockAdjustment ? 'Update stock adjustment details' : 'Add a new stock adjustment record')}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
              onClick={onCancel}
            >
              <span className="sr-only">Close</span>
              <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-4 pt-3 pb-2 space-y-4">
            {errors.submit && (
              <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded px-3 py-2">{errors.submit}</p>
            )}

            {/* Adjustment Details */}
            {sectionHeader('Adjustment Details')}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Item<span className="text-red-500 ml-0.5">*</span>
                </label>
                <SearchablePagedDropdown
                  value={formData.item}
                  displayLabel={itemLabel}
                  onChange={(id, label) => {
                    setItemLabel(label || '');
                    handleChange({ target: { name: 'item', value: id || '' } });
                  }}
                  fetchPage={fetchItemPage}
                  getLabel={(item) => `${item.name}${item.sku ? ` (${item.sku})` : ''}`}
                  getSubLabel={(item) => [item.category?.name, item.inventory?.unit || item.unit].filter(Boolean).join(' · ')}
                  noun="item"
                  placeholder="Select item…"
                  searchPlaceholder="Search by name, SKU or barcode…"
                  disabled={viewMode}
                  error={!!errors.item}
                  menuPlacement="down"
                />
                {errors.item && <p className="text-[10px] text-red-500 mt-0.5">{errors.item}</p>}
              </div>
              {select('Warehouse', 'warehouse', warehouseOptions, { required: true })}
              {select('Adjustment Type', 'adjustmentType', adjustmentTypeOptions, { required: true })}
            </div>

            <div className="grid grid-cols-2 gap-2">
              {formData.adjustmentType === 'set'
                ? field('Set Quantity To', 'newQuantity', 'number', { required: true, placeholder: '0', min: '0', step: '0.01', help: 'The exact quantity to set the stock to' })
                : field(`${formData.adjustmentType === 'increase' ? 'Increase' : 'Decrease'} By`, 'quantity', 'number', { required: true, placeholder: '0', min: '0', step: '0.01', help: `Quantity to ${formData.adjustmentType} stock by` })}
              {select('Reason Code', 'reasonCode', reasonCodeOptions, { required: true })}
            </div>

            {/* Stock Summary */}
            {formData.item && formData.warehouse && (
              <div className="grid grid-cols-3 gap-2 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                <div>
                  <span className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider">Current Stock</span>
                  <span className="text-sm font-bold text-gray-900">{currentStock}</span>
                </div>
                <div>
                  <span className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider">Change</span>
                  <span className={`text-sm font-bold ${formData.adjustmentType === 'decrease' ? 'text-red-600' : 'text-indigo-600'}`}>
                    {formData.adjustmentType === 'set'
                      ? `Set to ${formData.newQuantity || 0}`
                      : `${formData.adjustmentType === 'increase' ? '+' : '-'}${formData.quantity || 0}`}
                  </span>
                </div>
                <div>
                  <span className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider">New Stock</span>
                  <span className="text-sm font-bold text-green-700">{calculateNewStock()}</span>
                </div>
                {(() => {
                  const change = calculateNewStock() - currentStock;
                  if (!change) return null;
                  const cost = costUsed();
                  return (
                    <div className="col-span-3 border-t border-indigo-200 pt-2 text-xs text-gray-700">
                      Value: {change > 0 ? '+' : '−'}{Math.abs(change)} × {money(cost)} ={' '}
                      <span className={`font-semibold ${change > 0 ? 'text-indigo-700' : 'text-red-600'}`}>Rs {money(Math.abs(change) * cost)}</span>
                      <span className="text-gray-500"> · {change > 0 ? 'stock gain (Dr Inventory / Cr Stock Gain)' : 'stock loss (Dr Stock Loss / Cr Inventory)'} on approval</span>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Additional Information */}
            {sectionHeader('Additional Information')}
            {textarea('Reason', 'reason', { required: true, rows: 2, placeholder: 'Detailed reason for the stock adjustment…', help: 'Provide a clear explanation for this adjustment' })}

            <div className="grid grid-cols-2 gap-2">
              {textarea('Notes', 'notes', { rows: 2, placeholder: 'Additional notes…', help: 'Any additional information about this adjustment' })}
              {field('Reference Number', 'reference', 'text', { placeholder: 'REF-001', help: 'External reference number or document' })}
            </div>

            <div className="grid grid-cols-2 gap-2">
              {field('Adjustment Date', 'adjustmentDate', 'date', { required: true, help: 'Date the adjustment takes effect (e.g. the stock count date)' })}
              {formData.adjustmentType === 'increase'
                ? field('Unit Cost', 'unitCost', 'number', { placeholder: '0.00', step: '0.01', min: '0', right: true, help: costSource ? `${costSource} — can be changed` : 'Select item and warehouse to load the cost' })
                : (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Unit Cost</label>
                    <input type="text" readOnly value={averageCost ? money(averageCost) : ''} placeholder="—"
                      className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-gray-100 text-right text-gray-700" />
                    <p className="text-[10px] text-gray-400 mt-0.5">{costSource ? `${costSource} — a reduction is always valued at the average cost` : 'Select item and warehouse to load the cost'}</p>
                  </div>
                )}
            </div>
            {fullAdjustment?.glTransaction?.status && (
              <p className={`text-[11px] ${fullAdjustment.glTransaction.status === 'posted' ? 'text-green-700' : 'text-gray-500'}`}>
                GL entry: {fullAdjustment.glTransaction.status}{fullAdjustment.glTransaction.message ? ` — ${fullAdjustment.glTransaction.message}` : ''}
                {fullAdjustment.valueChange ? ` · value ${Number(fullAdjustment.valueChange).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
              </p>
            )}
          </div>

          {/* Footer with actions */}
          <div className="flex-shrink-0 bg-white px-6 py-4 border-t border-gray-200 shadow-lg">
            <div className="flex items-center justify-between">
              <button
                type="button"
                className="px-8 py-3 border border-gray-300 rounded-xl text-base font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-sm"
                onClick={onCancel}
              >
                {viewMode ? 'Close' : 'Cancel'}
              </button>
              {/* View: actions for a pending adjustment (shown by permission) */}
              {viewMode && (() => {
                const adj = fullAdjustment || stockAdjustment;
                if (!adj || adj.status !== 'pending') return null;
                return (
                  <div className="flex items-center gap-2">
                    {onReject && (
                      <PermissionGuard permission="INVENTORY:STOCK:APPROVE">
                        <button type="button" onClick={() => onReject(adj)}
                          className="px-5 py-3 border border-red-300 rounded-xl text-base font-medium text-red-700 bg-white hover:bg-red-50 transition-colors shadow-sm">
                          Reject
                        </button>
                      </PermissionGuard>
                    )}
                    {onEdit && (
                      <PermissionGuard permission="INVENTORY:STOCK:UPDATE">
                        <button type="button" onClick={() => onEdit(adj)}
                          className="px-5 py-3 border border-indigo-300 rounded-xl text-base font-medium text-indigo-700 bg-white hover:bg-indigo-50 transition-colors shadow-sm">
                          Edit
                        </button>
                      </PermissionGuard>
                    )}
                    {onApprove && (
                      <PermissionGuard permission="INVENTORY:STOCK:APPROVE">
                        <button type="button" onClick={() => onApprove(adj)}
                          className="px-6 py-3 bg-green-600 rounded-xl text-base font-medium text-white hover:bg-green-700 transition-colors shadow-lg">
                          Approve
                        </button>
                      </PermissionGuard>
                    )}
                  </div>
                );
              })()}
              {!viewMode && (
                <>
                {closedError && <p className="text-xs text-red-600 self-center">{closedError}</p>}
                <button
                  type="submit"
                  disabled={loading || !!closedError}
                  className="px-8 py-3 bg-indigo-600 border border-transparent rounded-xl text-base font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>{stockAdjustment ? 'Updating...' : 'Creating...'}</span>
                    </div>
                  ) : (
                    stockAdjustment ? 'Update Adjustment' : 'Create Adjustment'
                  )}
                </button>
                </>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StockAdjustmentForm;
